/**
 * AppProfileMenu
 * ==============
 * The account menu behind the navbar avatar, for all three shells. Supplies
 * identity, rows, drawers and footer to the `AppProfilePopover` shell, which
 * renders the DS `Menu / Profile` assembly.
 *
 * One menu, three surfaces. Admin, platform and My Outpave differ only in
 * where three of the rows point, which is read off the path rather than
 * passed in, so a shell that mounts the navbar gets the right menu without
 * wiring anything. Admin used to have its own copy in
 * `admin/common/ProfilePopover`; it was the last styled-components surface in
 * the chrome and painted its own white paper, so it went dark-mode blind.
 */
import React, { useState } from "react";
import Typography from "@mui/material/Typography";
import { useAuth, useFeatureEntitlements } from "@frontegg/react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Exchange01Icon,
  UserCircle02Icon,
  Key01Icon,
  BankIcon,
  Notification01Icon,
  PreferenceHorizontalIcon,
  GiftIcon,
  Logout01Icon,
} from "@hugeicons-pro/core-stroke-rounded";
import { Stack, Inline } from "components/layout/reusableLayouts";
import AppProfilePopover, {
  AppProfileMenuItem,
  AppProfileMenuSection,
} from "components/common/AppProfilePopover";
import PasswordDrawer from "components/common/users/PasswordDrawer";
import UserProfileDrawer from "components/common/users/UserProfileDrawer";
import PersonalBankAccountDrawer from "components/common/users/PersonalBankAccountDrawer";
import useGetParamFromUrl from "hooks/useGetParamFromUrl";
import { useDrawerRecord } from "hooks/useDrawerRecord";
import ModeSwitchButton from "components/common/ModeSwitchButton";
import PreferencesDrawer from "components/common/users/PreferencesDrawer";
import Role from "enums/Role";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import { useAppEntitlements } from "hooks/useAppEntitlements";
import { useBrandingContext } from "context/BrandingContext";
import { fullName } from "utils/fullName";

interface AppProfileMenuProps {
  id?: string;
  open: boolean;
  anchorEl: HTMLElement | null;
  handleClose: () => void;
  // Narrow to just the fields we actually read on the menu. The legacy
  // component took an opaque `any`; we don't need its full shape.
  userData?: {
    user?: {
      id?: string;
      email?: string;
      firstName?: string;
      lastName?: string | null;
      phone?: string;
      profilePicUrl?: string | null;
      company?: {
        businessDBA?: string;
        businessLegalName?: string;
      };
    };
  };
}

const externalLinkSx = {
  color: "var(--color-text-tertiary)",
  cursor: "pointer",
  textDecoration: "none",
  "&:hover": { color: "var(--color-text-primary)" },
} as const;

const AppProfileMenu: React.FC<AppProfileMenuProps> = ({
  id,
  open,
  anchorEl,
  handleClose,
  userData,
}) => {
  const { t } = useTranslation();
  const { platform } = useBrandingContext();
  const { isEntitled: featureNotification } = useFeatureEntitlements(
    "feature_notifications",
  );
  const { user } = useAuth();
  const isPlatformAdmin = useCheckUserRole(Role.PlatformAdmin);
  const { spendApp: hasSpendApp, platform: hasAppPlatform } =
    useAppEntitlements();

  const location = useLocation();
  const navigate = useNavigate();

  const isPlatformPages = location.pathname.includes("/platform");
  const isAdminPages = location.pathname.startsWith("/admin");
  const canSwitch =
    (isPlatformPages && hasSpendApp) || (!isPlatformPages && hasAppPlatform);

  const [openPasswordDrawer, setOpenPasswordDrawer] = useState(false);
  const profileDrawer = useDrawerRecord<true>();
  const [openPreferencesDrawer, setOpenPreferencesDrawer] = useState(false);
  const [openUserBankAccountsDrawer, setOpenUserBankAccountsDrawer] =
    useState(false);

  const resetBankParam = useGetParamFromUrl("personalBankAccount", (value) => {
    if (value) {
      setOpenUserBankAccountsDrawer(true);
      resetBankParam();
    }
  });

  // Navigate then close, so the menu dismisses on every routing item.
  const go = (to: string, state?: unknown) => () => {
    navigate(to, state ? { state } : undefined);
    handleClose();
  };

  // Each shell keeps its own notification settings page.
  const notificationsRoute = isPlatformPages
    ? "/platform/notifications"
    : isAdminPages
      ? "/admin/user-notifications"
      : "/my/notifications";

  const switchItem: AppProfileMenuItem | null =
    isPlatformAdmin && canSwitch
      ? {
          icon: Exchange01Icon,
          label: isPlatformPages
            ? t("Switch to Spend")
            : t("Switch to Platform"),
          onClick: go(
            isPlatformPages ? "/admin/dashboard" : "/platform/dashboard",
          ),
          external: true,
        }
      : null;

  const accountSection: Array<AppProfileMenuItem | false | null> = [
    {
      icon: UserCircle02Icon,
      label: t("Profile"),
      onClick: () => {
        profileDrawer.show(true);
        handleClose();
      },
    },
    {
      icon: PreferenceHorizontalIcon,
      label: t("Preferences"),
      onClick: () => {
        setOpenPreferencesDrawer(true);
        handleClose();
      },
    },
    {
      icon: Key01Icon,
      label: t("Password"),
      onClick: () => {
        setOpenPasswordDrawer(true);
        handleClose();
      },
    },
    {
      icon: BankIcon,
      label: t("Bank account"),
      onClick: () => {
        setOpenUserBankAccountsDrawer(true);
        handleClose();
      },
    },
    featureNotification && {
      icon: Notification01Icon,
      label: t("Notification settings"),
      onClick: go(notificationsRoute),
      // Admin's settings page sits outside the admin nav, which is why its
      // menu has always marked this row as leaving the view.
      external: isAdminPages || undefined,
    },
    {
      icon: GiftIcon,
      label: t("Refer a company"),
      onClick: go(isPlatformPages ? "/platform/refer" : "/admin/refer", {
        company: userData?.user?.company?.businessLegalName,
        email: userData?.user?.email,
        firstname: userData?.user?.firstName,
        lastname: userData?.user?.lastName,
        phone: userData?.user?.phone,
      }),
      external: true,
      // Admin is the only surface that has ever offered this.
      hidden: !isAdminPages,
    },
  ];

  // Admin only: the employee half of the same account, which the admin menu
  // has always linked to and the other two shells are already inside.
  const myOutpaveItem: AppProfileMenuItem | null = isAdminPages
    ? {
        icon: Exchange01Icon,
        label: t("Switch to My Outpave"),
        onClick: go("/my/dashboard"),
        external: true,
      }
    : null;

  const themeSection: AppProfileMenuSection = {
    content: (
      <Stack sx={{ padding: "var(--space-2) var(--space-3)" }}>
        <ModeSwitchButton />
      </Stack>
    ),
  };

  // A branded tenant points at its own policies. The platform menu used to
  // hardcode outpave.com here, which is wrong on every white-label shell.
  const privacyUrl =
    platform?.privacyUrl || "https://outpave.com/privacy-and-security";
  const termsUrl = platform?.termsUrl || "https://outpave.com/user-terms";

  const footer = (
    <Stack
      gap="var(--space-2)"
      sx={{ padding: "var(--space-3) var(--space-3)" }}
    >
      <Typography
        variant="caption"
        sx={{ color: "var(--color-text-tertiary)" }}
      >
        {process.env.VERSION}
      </Typography>
      <Inline gap="var(--space-3)">
        <Typography
          variant="caption"
          component="a"
          href={privacyUrl}
          target="_blank"
          rel="noreferrer"
          sx={externalLinkSx}
        >
          {t("Privacy")}
        </Typography>
        <Typography
          variant="caption"
          component="a"
          href={termsUrl}
          target="_blank"
          rel="noreferrer"
          sx={externalLinkSx}
        >
          {t("Terms and Conditions")}
        </Typography>
      </Inline>
    </Stack>
  );

  return (
    <>
      <AppProfilePopover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        name={fullName(userData?.user) || user?.name}
        company={userData?.user?.company?.businessDBA}
        avatarSrc={userData?.user?.profilePicUrl}
        sections={[
          accountSection,
          [switchItem, myOutpaveItem],
          themeSection,
          [
            {
              icon: Logout01Icon,
              label: t("Logout"),
              onClick: go("/logout"),
            },
          ],
        ]}
        footer={footer}
      />

      <PasswordDrawer
        open={openPasswordDrawer}
        handleCloseDrawer={() => setOpenPasswordDrawer(false)}
      />
      {profileDrawer.record && (
        <UserProfileDrawer
          open={profileDrawer.open}
          selectedUserId={userData?.user?.id}
          handleCloseDrawer={profileDrawer.close}
          onAfterClose={profileDrawer.afterClose}
        />
      )}
      <PreferencesDrawer
        open={openPreferencesDrawer}
        handleCloseDrawer={() => setOpenPreferencesDrawer(false)}
      />
      <PersonalBankAccountDrawer
        open={openUserBankAccountsDrawer}
        handleCloseDrawer={() => setOpenUserBankAccountsDrawer(false)}
      />
    </>
  );
};

export default AppProfileMenu;
